import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import moment from 'moment';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
  // customMessageShackBar,
  customErrorShackBar,
} from '../../../../actions/snackbars/SnackBarsActions';
import { useAuthUserId } from '../../../../selectors/AuthSelectors';
import { useRestaurantId } from '../../../../selectors/RestaurantSelectors';
import * as Orders from '../../../entities/orders';
import { formatSystemDateTime } from '../../../shared/lib';
import {
  getActiveInPeriodRequest,
  getUnknownGuestArrivedRequest,
  getGuestArrivedRequest,
  getGuestLeftRequest,
  getCancelReserveRequest,
  getMergeReserveWithOrderRequest,
} from '../api';

export function getActiveInPeriodQueryKey(
  userId: number,
  restaurantId: number,
  from: moment.MomentInput | undefined = undefined,
  till: moment.MomentInput | undefined = undefined,
) {
  const items = [userId, 'restaurant', restaurantId, 'active_booking'];

  if (from) {
    items.push(formatSystemDateTime(from));
  }

  if (till) {
    items.push(formatSystemDateTime(till));
  }

  return items;
}

export function useActiveInPeriodQueryKey(
  from: moment.MomentInput,
  till: moment.MomentInput | undefined = undefined,
) {
  return getActiveInPeriodQueryKey(useAuthUserId(), useRestaurantId(), from, till);
}

export function useActiveInPeriod(from: number, till: number) {
  const f = moment(from);
  const t = moment(till);
  const restaurantId = useRestaurantId();
  // const queryKey = useActiveInPeriodQueryKey(f, t);
  const queryKey = useActiveInPeriodQueryKey(f);

  const {
    data: response,
    isLoading,
    isFetching,
    isFetched,
  } = useQuery({
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    queryKey,
    queryFn: () => {
      return getActiveInPeriodRequest(restaurantId, f, t);
    },
  });

  const data = useMemo(() => {
    if (!response?.data) {
      return null;
    }

    return response.data.filter((item) => item.tables?.length > 0);
  }, [response?.data]);

  return {
    isFetched,
    isLoading: isLoading || isFetching,
    data,
  };
}

export function useGuestArrivedMutation(from: number) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const queryKeyToReset = useActiveInPeriodQueryKey(moment(from));

  return useMutation<
    GM.Booking.Response.GuestArrived,
    AxiosError,
    { tableId: number } | { reserveId: number }
  >({
    mutationFn: (payload) => {
      if ('tableId' in payload) {
        return getUnknownGuestArrivedRequest(payload.tableId);
      } else {
        return getGuestArrivedRequest(payload.reserveId);
      }
    },

    onSuccess: () => {
      // onSuccess: (data, variables) => {
      queryClient.refetchQueries({ queryKey: queryKeyToReset });

      // if ('tableId' in variables) {
      //   dispatch(customMessageShackBar('Резерв добавлен'));
      // } else {
      //   dispatch(customMessageShackBar('Гости пришли'));
      // }
    },

    onError: () => {
      // onError: (error, variables) => {
      // if ('tableId' in variables) {
      //   dispatch(customErrorShackBar('Ошибка при добавлении резерва'));
      // } else {
      //   dispatch(customErrorShackBar('Ошибка обновления резерва'));
      // }
      dispatch(customErrorShackBar('Ошибка'));
    },
  });
}

export function useGuestLeftMutation(from: number) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const queryKeyToReset = useActiveInPeriodQueryKey(moment(from));

  return useMutation<GM.Booking.Response.GuestLeft, AxiosError, number>({
    mutationFn: (requestId) => {
      return getGuestLeftRequest(requestId);
    },

    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: queryKeyToReset });
      // dispatch(customMessageShackBar('Гости ушли'));
    },

    onError: () => {
      // dispatch(customErrorShackBar('Ошибка обновления'));
      dispatch(customErrorShackBar('Ошибка'));
    },
  });
}

// export function useCancelReserveMutation(from: number, till: number) {
export function useCancelReserveMutation(from: number) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  // const queryKey = useActiveInPeriodQueryKey(moment(from), moment(till));
  const queryKey = useActiveInPeriodQueryKey(moment(from));

  return useMutation<GM.Booking.Response.Cancel, AxiosError, number>({
    mutationFn: (reserveId: number) => {
      return getCancelReserveRequest(reserveId);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey });
      // dispatch(customMessageShackBar('Резерв отменён'));
    },
    onError: () => {
      // dispatch(customErrorShackBar('Ошибка отмены резерва'));
      dispatch(customErrorShackBar('Ошибка'));
    },
  });
}

export function useMergeReserveWithOrderMutation(from: number) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  // const queryKey = useActiveInPeriodQueryKey(moment(from), moment(till));
  const queryKey = useActiveInPeriodQueryKey(moment(from));
  const ordersQueryKey = Orders.useActiveInPeriodQueryKey(moment(from));

  return useMutation<GM.Booking.Response.Merge, AxiosError, { reserveId: number; orderId: number }>(
    {
      mutationFn: (payload) => {
        const { reserveId, orderId } = payload;
        return getMergeReserveWithOrderRequest(reserveId, orderId);
      },
      onSuccess: () => {
        queryClient.refetchQueries({ queryKey: ordersQueryKey });
        queryClient.refetchQueries({ queryKey });
        // dispatch(customMessageShackBar('Резерв и заказ объединены'));
      },
      onError: () => {
        dispatch(customErrorShackBar('Ошибка'));
      },
    },
  );
}
